import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ProductDemo = () => {
  const [currentStep, setCurrentStep] = useState(1);
  
  // Auto-advance through steps for demo purposes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentStep(1); // Reset to beginning
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [currentStep]);

  return (
    <section className="py-16 px-6 bg-gray-50">
      <div className="container mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-midnight-navy">
          From Equity Pick to Full Report — In Minutes.
        </h2>
        <p className="text-center text-gray-700 mb-12 max-w-2xl mx-auto">
          See how Research Genie transforms your workflow.
        </p>
        
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <div className="ml-4 text-sm text-gray-500">Research Genie Demo</div>
            </div>
            
            <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6">
              {/* Step 1: Select Ticker */}
              <motion.div 
                className={`md:w-1/4 p-4 border rounded-md ${currentStep === 1 ? 'border-pale-gold ring-2 ring-pale-gold ring-opacity-50' : 'border-gray-200'}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
                onClick={() => setCurrentStep(1)}
              >
                <div className="text-sm font-semibold text-gray-500 mb-2">Step 1</div>
                <h3 className="text-lg font-medium mb-2 text-midnight-navy">Select ticker</h3>
                <div className="bg-gray-100 p-2 rounded">
                  <div className="flex items-center justify-between">
                    <span className="font-mono">AAPL</span>
                    <span className="text-green-600">+1.2%</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Step 2: Summon Genie */}
              <motion.div 
                className={`md:w-1/4 p-4 border rounded-md ${currentStep === 2 ? 'border-pale-gold ring-2 ring-pale-gold ring-opacity-50' : 'border-gray-200'}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                onClick={() => setCurrentStep(2)}
              >
                <div className="text-sm font-semibold text-gray-500 mb-2">Step 2</div>
                <h3 className="text-lg font-medium mb-2 text-midnight-navy">Summon Genie</h3>
                <div className="bg-gray-100 p-2 rounded">
                  <div className="flex items-center">
                    <span className="text-pale-gold mr-2">✨</span>
                    <span className="text-sm italic">Summoning your analysis...</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Step 3: Preview Results */}
              <motion.div 
                className={`md:w-1/4 p-4 border rounded-md ${currentStep === 3 ? 'border-pale-gold ring-2 ring-pale-gold ring-opacity-50' : 'border-gray-200'}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
                onClick={() => setCurrentStep(3)}
              >
                <div className="text-sm font-semibold text-gray-500 mb-2">Step 3</div>
                <h3 className="text-lg font-medium mb-2 text-midnight-navy">See preview</h3>
                <div className="bg-gray-100 p-2 rounded text-xs">
                  <div className="mb-1 font-semibold">Key Metrics:</div>
                  <div className="grid grid-cols-2 gap-1">
                    <div>P/E: 28.5</div>
                    <div>EPS: $6.14</div>
                    <div>Rev: $394.3B</div>
                    <div>Mkt Cap: $2.8T</div>
                  </div>
                </div>
              </motion.div>
              
              {/* Step 4: Export */}
              <motion.div 
                className={`md:w-1/4 p-4 border rounded-md ${currentStep === 4 ? 'border-pale-gold ring-2 ring-pale-gold ring-opacity-50' : 'border-gray-200'}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                viewport={{ once: true }}
                onClick={() => setCurrentStep(4)}
              >
                <div className="text-sm font-semibold text-gray-500 mb-2">Step 4</div>
                <h3 className="text-lg font-medium mb-2 text-midnight-navy">Export report</h3>
                <div className="flex space-x-2">
                  <div className="bg-gray-100 p-2 rounded text-xs flex items-center">
                    <span className="mr-1">📄</span> PDF
                  </div>
                  <div className="bg-gray-100 p-2 rounded text-xs flex items-center">
                    <span className="mr-1">📊</span> PPT
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
          
          <div className="p-6 bg-gray-50">
            <AnimatePresence mode="wait">
              <motion.div 
                key={currentStep}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-48 bg-gray-200 rounded-md flex items-center justify-center"
              >
                {currentStep === 1 && (
                  <div className="text-center">
                    <div className="text-midnight-navy text-4xl mb-2">🔍</div>
                    <div className="text-midnight-navy font-medium">Select Your Company</div>
                    <div className="text-sm text-gray-500">Choose from thousands of public companies</div>
                  </div>
                )}
                
                {currentStep === 2 && (
                  <div className="text-center">
                    <div className="text-pale-gold text-4xl mb-2 animate-pulse">✨</div>
                    <div className="text-midnight-navy font-medium">Summoning Research Genie</div>
                    <div className="text-sm text-gray-500">Your wish is our command</div>
                  </div>
                )}
                
                {currentStep === 3 && (
                  <div className="text-center">
                    <div className="text-electric-blue text-4xl mb-2">📊</div>
                    <div className="text-midnight-navy font-medium">Instant Analysis</div>
                    <div className="text-sm text-gray-500">Key metrics, risks, and insights</div>
                  </div>
                )}
                
                {currentStep === 4 && (
                  <div className="text-center">
                    <div className="text-pale-gold text-4xl mb-2">📑</div>
                    <div className="text-midnight-navy font-medium">Export Ready Reports</div>
                    <div className="text-sm text-gray-500">PDF, PowerPoint, and more</div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDemo;
